import React from 'react'
import { Box, Button } from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'

import { AppFilterPanel, filterPanelGridSx, filterSearchButtonProps, secondaryButtonSx } from '@components'
import DownloadCsvButton from 'Pages/MyBusiness/_shared/Charts/DownloadCsvButton'

import AsOfSlicer        from './AsOfSlicer'
import CategorySlicer    from './CategorySlicer'
import ChannelSlicer     from './ChannelSlicer'
import { ComparePills, CompareRangeField } from './ComparisonPicker'
import { DatePresets, DateRangeField, rangeForPreset } from './DateRangeFilter'
import DaysBucketsSlicer from './DaysBucketsSlicer'
import DurationSlicer    from './DurationSlicer'
import InsuranceSlicer   from './InsuranceSlicer'
import OfficeSlicer      from './OfficeSlicer'
import PartnerSlicer     from './PartnerSlicer'
import ProrateSlicer     from './ProrateSlicer'

/**
 * Global filter bar for the My Business module. Used by every screen.
 *
 *   value     = draft filters (what the user is composing)
 *   applied   = filters currently reflected by the data on screen
 *   onChange(nextDraft)   — accumulate a pending change
 *   onApply(nextApplied)  — push to backend now
 *
 * Intent model
 * ------------
 * Each child component reports its intent on every change:
 *   - 'instant' (preset click, compare-mode dropdown) → onApply fires
 *   - 'pending' (typing dates, ticking slicers)       → onChange fires
 *
 * Pending changes accumulate in the draft. The Apply button shows the
 * count of fields that differ from `applied` and is only enabled when
 * there is something to apply.
 */
export function defaultFilters() {
	return {
		date:       { preset: 'ytd', ...rangeForPreset('ytd') },
		offices:    [],
		categories: [],
		// PY comparison is the project-wide default for all dashboards.
		compare:    { mode: 'py', from: '', to: '' },
		// 'total' = both Short and Long combined; backend leaves the
		// duration clause off entirely when this is the value.
		duration:    'total',
		// Specific-day buckets (1, 2, …, 8-14, 30+). Empty = no filter.
		days_buckets: [],
		// Revenue attribution. 'per_checkout' = pickup-date, 'per_day' = spread.
		prorate:      'per_checkout',
		// Market-only: 'all' | 'no_excess' | 'with_excess'. Module 1
		// endpoints ignore the param.
		insurance:    'all',
		// Channel-type multi-select (e.g. 'B2CWEB','Broker'). Empty = no filter.
		channels:     [],
		// Partner-code multi-select (PartnerNameShort). Empty = no filter.
		partners:     [],
		// OTB reconstruction date. Empty = live view.
		as_of:        '',
	}
}

export function filtersToQuery(f) {
	const qs = new URLSearchParams({
		from: f.date.from,
		to:   f.date.to,
	})
	if (f.offices.length)    qs.set('offices', f.offices.join(','))
	if (f.categories.length) qs.set('sipps',   f.categories.join(','))
	if (f.compare.mode !== 'none') {
		qs.set('compare', f.compare.mode)
		if (f.compare.mode === 'custom') {
			qs.set('compare_from', f.compare.from)
			qs.set('compare_to',   f.compare.to)
		}
	}
	if (f.duration && f.duration !== 'total') qs.set('duration', f.duration)
	if (f.days_buckets?.length) qs.set('days_buckets', f.days_buckets.join(','))
	if (f.prorate && f.prorate !== 'per_checkout') qs.set('prorate', f.prorate)
	if (f.insurance && f.insurance !== 'all') qs.set('insurance', f.insurance)
	if (f.channels?.length) qs.set('channel_types', f.channels.join(','))
	if (f.partners?.length) qs.set('partners',      f.partners.join(','))
	if (f.as_of)            qs.set('as_of',         f.as_of)
	return qs.toString()
}

/**
 * Count how many top-level fields differ between applied and draft.
 * Used to drive the Apply badge.
 */
export function pendingCount(applied, draft) {
	let n = 0
	if (applied.date.from !== draft.date.from || applied.date.to !== draft.date.to) n++
	if (applied.offices.join(',')    !== draft.offices.join(','))    n++
	if (applied.categories.join(',') !== draft.categories.join(',')) n++
	if (applied.compare.mode !== draft.compare.mode
	    || applied.compare.from !== draft.compare.from
	    || applied.compare.to   !== draft.compare.to) n++
	if ((applied.duration || 'total') !== (draft.duration || 'total')) n++
	if ((applied.days_buckets || []).join(',') !== (draft.days_buckets || []).join(',')) n++
	if ((applied.prorate || 'per_checkout') !== (draft.prorate || 'per_checkout')) n++
	if ((applied.insurance || 'all') !== (draft.insurance || 'all')) n++
	if ((applied.channels || []).join(',') !== (draft.channels || []).join(',')) n++
	if ((applied.partners || []).join(',') !== (draft.partners || []).join(',')) n++
	if ((applied.as_of || '') !== (draft.as_of || '')) n++
	return n
}

/**
 * Decide whether the current draft can be applied (i.e. Custom-range
 * comparison has both dates filled).
 */
function isApplyable(draft) {
	if (!draft.date.from || !draft.date.to) return false
	if (draft.compare.mode === 'custom' &&
	    (!draft.compare.from || !draft.compare.to)) return false
	return true
}

// The fields of the band: four per row, every slicer as wide as its column
export const mbFieldsGridSx = (columns = 4) => ({
	...filterPanelGridSx(columns),
	'& .MuiAutocomplete-root, & .MuiTextField-root, & .MuiFormControl-root': { width: 1, minWidth: '0 !important', maxWidth: 'none !important' },
	'& .MuiInputBase-root': { minHeight: 40 },
})

// The Apply button of the band: the Search button of the other pages, with its texts
export function ApplyButton({ onClick, disabled, children }) {
	return <Button variant="contained" onClick={onClick} disabled={disabled} {...filterSearchButtonProps}
	               sx={{ ...filterSearchButtonProps.sx, textTransform: 'none', fontSize: 14, '&.Mui-disabled': { boxShadow: 'none' } }}>
		{children}
	</Button>
}

export default function FiltersBar({ value, applied, onChange, onApply, loading,
                                     exportHref, csvHref, csvLabel = 'Raw CSV',
                                     showInsurance = false }) {
	// Route each child's change through this dispatcher so the intent
	// flag decides instant vs pending.
	const set = (patch, opts) => {
		const next = { ...value, ...patch }
		if (opts?.intent === 'instant') onApply(next)
		else                            onChange(next)
	}

	const pending  = pendingCount(applied, value)
	const canApply = pending > 0 && isApplyable(value) && !loading

	return (
		<AppFilterPanel
			toggles={<DatePresets value={value.date} onChange={(date, opts) => set({ date }, opts)}/>}
			togglesRight={<ComparePills value={value.compare} onChange={(compare, opts) => set({ compare }, opts)}/>}
			actions={<>
				{exportHref && (
					<Button variant="outlined" sx={secondaryButtonSx}
					        startIcon={<IconSeeker icon="file-excel" sx={{ fontSize: 14 }}/>}
					        component="a"
					        href={exportHref}>
						Excel
					</Button>
				)}
				{csvHref && (
					<DownloadCsvButton href={csvHref}
					                   from={applied?.date?.from}
					                   to={applied?.date?.to}
					                   label={csvLabel}/>
				)}
				<ApplyButton onClick={() => onApply(value)} disabled={!canApply}>
					{loading ? 'Loading…'
					         : pending > 0 ? `Apply (${pending})`
					         : 'Up to date'}
				</ApplyButton>
			</>}
		>
			<Box sx={mbFieldsGridSx()}>
				<DateRangeField value={value.date}
				                onChange={(date, opts) => set({ date }, opts)}/>
				<OfficeSlicer    value={value.offices}
				                 onChange={(offices, opts) => set({ offices }, opts)}/>
				<CategorySlicer  value={value.categories}
				                 onChange={(categories, opts) => set({ categories }, opts)}/>
				<ChannelSlicer    value={value.channels || []}
				                  onChange={(channels, opts) => set({ channels }, opts)}/>
				<PartnerSlicer    value={value.partners || []}
				                  onChange={(partners, opts) => set({ partners }, opts)}/>
				<DaysBucketsSlicer value={value.days_buckets || []}
				                   onChange={(days_buckets, opts) => set({ days_buckets }, opts)}/>
				<DurationSlicer  value={value.duration || 'total'}
				                 onChange={(duration, opts) => set({ duration }, opts)}/>
				<ProrateSlicer    value={value.prorate || 'per_checkout'}
				                  onChange={(prorate, opts) => set({ prorate }, opts)}/>
				<AsOfSlicer       value={value.as_of || ''}
				                  onChange={(as_of, opts) => set({ as_of }, opts)}/>
				{showInsurance && (
					<InsuranceSlicer value={value.insurance || 'all'}
					                 onChange={(insurance, opts) => set({ insurance }, opts)}/>
				)}
				<CompareRangeField value={value.compare}
				                   onChange={(compare, opts) => set({ compare }, opts)}/>
			</Box>
		</AppFilterPanel>
	)
}
